import React, { useEffect, useMemo, useRef, useState } from "react";
import { useAuth } from "../../context/AuthContext";
import { useData } from "../../context/DataContext";
import { exportPdf } from "../reports/reportExport";
import {
  getInvestigations,
  rejectInvestigationRequest,
  reviewInvestigation,
} from "../../api/investigations";
import { isHrFamilyRole, isOwnerRole } from "../../utils/hrStaffHelpers";
import InvestigationEditor from "./InvestigationEditor";
import {
  INVESTIGATION_DRAFT_KEY,
  INVESTIGATION_FOCUS_KEY,
} from "./investigationDraft";
import {
  FILTERS,
  decisionLabel,
  formatSession,
  formatParticipantLine,
  penaltyOutcomeLabel,
  getApiErrorMessage,
  participantScheduleLines,
  statusLabel,
} from "./investigationLabels";
import "./InvestigationsPage.css";

function Field({ title, value }) {
  if (!value) return null;
  return (
    <section className="inv-section">
      <h3>{title}</h3>
      <p>{value}</p>
    </section>
  );
}

export default function InvestigationsPage() {
  const { user } = useAuth();
  const { employeesByBranch } = useData();
  const printRef = useRef(null);
  const canManage = isHrFamilyRole(user?.role);
  const canDecide = user?.role === "gm";
  const canReadText = canManage || canDecide;
  const canDownload =
    canManage || canDecide || isOwnerRole(user?.role) || user?.role === "admin";
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [filter, setFilter] = useState("all");
  const [selectedId, setSelectedId] = useState(null);
  const [editor, setEditor] = useState(null);
  const [reviewNote, setReviewNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState("");

  const employees = useMemo(
    () => Object.values(employeesByBranch || {}).flat(),
    [employeesByBranch],
  );

  async function load() {
    setLoading(true);
    setError("");
    try {
      const rows = await getInvestigations();
      setItems(Array.isArray(rows) ? rows : []);
    } catch (err) {
      setError(getApiErrorMessage(err, "تعذر تحميل التحقيقات"));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
  }, []);

  useEffect(() => {
    function consumeFocus() {
      const id = sessionStorage.getItem(INVESTIGATION_FOCUS_KEY);
      if (!id) return;
      sessionStorage.removeItem(INVESTIGATION_FOCUS_KEY);
      setSelectedId(id);
      setEditor(null);
    }
    consumeFocus();
    window.addEventListener("investigation:focus", consumeFocus);
    return () => window.removeEventListener("investigation:focus", consumeFocus);
  }, []);

  useEffect(() => {
    if (!canManage) return;
    const raw = sessionStorage.getItem(INVESTIGATION_DRAFT_KEY);
    if (!raw) return;
    sessionStorage.removeItem(INVESTIGATION_DRAFT_KEY);
    try {
      const draft = JSON.parse(raw);
      if (draft?.complaintId) {
        setEditor({
          mode: "create",
          complaintId: draft.complaintId,
          complaintTitle: draft.title || "",
        });
        setSelectedId(null);
      }
    } catch {
      sessionStorage.removeItem(INVESTIGATION_DRAFT_KEY);
    }
  }, [canManage]);

  const visible = items.filter((item) => filter === "all" || item.status === filter);
  const selected = items.find((item) => item.id === selectedId) || null;

  function replaceItem(saved) {
    if (!saved?.id) return;
    setItems((prev) => {
      const exists = prev.some((item) => item.id === saved.id);
      const next = exists
        ? prev.map((item) => (item.id === saved.id ? saved : item))
        : [saved, ...prev];
      return next.sort((a, b) => String(b.code).localeCompare(String(a.code)));
    });
    setSelectedId(saved.id);
    setEditor(null);
    setReviewNote("");
    setActionError("");
  }

  async function runAction(work) {
    setBusy(true);
    setActionError("");
    try {
      replaceItem(await work());
    } catch (err) {
      setActionError(getApiErrorMessage(err, "تعذر تنفيذ الإجراء"));
    } finally {
      setBusy(false);
    }
  }

  async function download() {
    if (!printRef.current || !selected) return;
    setActionError("");
    try {
      await exportPdf(printRef.current, `تحقيق-${selected.code}`, {
        orientation: "portrait",
      });
    } catch (err) {
      setActionError(getApiErrorMessage(err, "تعذر تحميل ملف التحقيق"));
    }
  }

  const subjectLine = selected
    ? formatParticipantLine(
        (selected.participants || []).find((person) => person.kind === "SUBJECT") || {
          name: selected.subjectEmployeeName,
          sessionDate: selected.sessionDate,
          sessionStart: selected.sessionStart,
          sessionEnd: selected.sessionEnd,
        },
        selected,
      )
    : "";
  const partyLines = selected ? participantScheduleLines(selected, "PARTY") : [];
  const witnessLines = selected
    ? participantScheduleLines(selected, "WITNESS")
    : [];
  const parties =
    partyLines.length > 0
      ? partyLines.join("\n")
      : selected?.partiesText || "";
  const witnesses =
    witnessLines.length > 0
      ? witnessLines.join("\n")
      : selected?.witnessText || "";

  return (
    <div className="inv-page" dir="rtl">
      <header className="inv-header">
        <div>
          <h1 className="inv-title">التحقيقات</h1>
          <p className="inv-subtitle">
            كل تحقيق برقم. الملخص يظهر هنا، والتفاصيل كاملة عند الضغط.
          </p>
        </div>
        {canManage && (
          <button
            type="button"
            className="inv-btn primary"
            onClick={() => {
              setSelectedId(null);
              setEditor({ mode: "create" });
            }}
          >
            فتح تحقيق
          </button>
        )}
      </header>

      {editor ? (
        <InvestigationEditor
          mode={editor.mode}
          investigation={editor.investigation || selected}
          complaintId={editor.complaintId || selected?.complaintId}
          complaintTitle={editor.complaintTitle || selected?.complaintTitle}
          employees={employees}
          investigatorDefault={user?.name || ""}
          onClose={() => setEditor(null)}
          onSaved={replaceItem}
        />
      ) : null}

      {!selected && !editor && (
        <>
          <div className="inv-filters">
            {FILTERS.map((entry) => (
              <button
                key={entry.id}
                type="button"
                className={`inv-filter ${filter === entry.id ? "active" : ""}`}
                onClick={() => setFilter(entry.id)}
              >
                {entry.label}
              </button>
            ))}
          </div>
          {loading ? <p>جارٍ تحميل التحقيقات...</p> : null}
          {error ? <p className="inv-error">{error}</p> : null}
          {!loading && !error && visible.length === 0 ? (
            <p>لا توجد تحقيقات في هذا التصنيف.</p>
          ) : (
            <div className="inv-list">
              {visible.map((item) => (
                <button
                  key={item.id}
                  type="button"
                  className="inv-summary"
                  onClick={() => setSelectedId(item.id)}
                >
                  <div className="inv-summary-top">
                    <span className="inv-code">تحقيق رقم {item.code}</span>
                    <span className={`inv-badge ${item.status}`}>
                      {statusLabel(item.status)}
                    </span>
                  </div>
                  <p className="inv-summary-text">
                    {item.incident || item.requestNote || item.complaintTitle || "بانتظار استكمال البيانات"}
                  </p>
                  <div className="inv-meta">
                    <span>{formatSession(item)}</span>
                    {item.subjectEmployeeName ? (
                      <span>الحضور: {item.subjectEmployeeName}</span>
                    ) : null}
                    {canReadText && decisionLabel(item.decision) ? (
                      <span>{decisionLabel(item.decision)}</span>
                    ) : penaltyOutcomeLabel(item) ? (
                      <span>{penaltyOutcomeLabel(item)}</span>
                    ) : null}
                  </div>
                </button>
              ))}
            </div>
          )}
        </>
      )}

      {selected && !editor && (
        <article className="inv-detail">
          <div className="inv-detail-top">
            <button
              type="button"
              className="inv-btn"
              onClick={() => setSelectedId(null)}
            >
              كل التحقيقات
            </button>
            <div className="inv-actions">
              {canDownload && (
                <button type="button" className="inv-btn" onClick={download}>
                  تحميل
                </button>
              )}
              {canManage && selected.status === "REQUESTED" && (
                <button
                  type="button"
                  className="inv-btn primary"
                  onClick={() =>
                    setEditor({
                      mode: "schedule",
                      investigation: selected,
                      complaintId: selected.complaintId,
                      complaintTitle: selected.complaintTitle,
                    })
                  }
                >
                  تحديد الموعد
                </button>
              )}
              {canManage &&
                (selected.status === "SCHEDULED" ||
                  selected.status === "RETURNED") && (
                  <>
                    <button
                      type="button"
                      className="inv-btn"
                      onClick={() =>
                        setEditor({ mode: "edit", investigation: selected })
                      }
                    >
                      تعديل البيانات
                    </button>
                    <button
                      type="button"
                      className="inv-btn primary"
                      onClick={() =>
                        setEditor({ mode: "decision", investigation: selected })
                      }
                    >
                      تسجيل النتيجة
                    </button>
                  </>
                )}
            </div>
          </div>

          <div ref={printRef} className="inv-print">
            <div className="inv-summary-top">
              <h2>تحقيق رقم {selected.code}</h2>
              <span className={`inv-badge ${selected.status}`}>
                {statusLabel(selected.status)}
              </span>
            </div>
            <div className="inv-meta">
              {selected.complaintTitle ? (
                <span>الشكوى: {selected.complaintTitle}</span>
              ) : null}
              {selected.requestedByName ? (
                <span>مقدم الطلب: {selected.requestedByName}</span>
              ) : null}
              {selected.openedByName ? (
                <span>فتحه: {selected.openedByName}</span>
              ) : null}
            </div>
            <Field title="موعد الموظف المطلوب" value={subjectLine} />
            <Field title="طلب فتح التحقيق" value={selected.requestNote} />
            <Field title="الواقعة" value={selected.incident} />
            <Field title="القائم بالتحقيق" value={selected.investigatorName} />
            <Field title="الأطراف" value={parties} />
            <Field title="الشاهد" value={witnesses} />
            {canReadText ? (
              <>
                <Field title="نص التحقيق" value={selected.minutes} />
                <Field title="النتيجة" value={selected.result} />
                <Field title="التوصيات" value={selected.recommendations} />
                <Field title="القرار" value={decisionLabel(selected.decision)} />
                <Field title="تفاصيل القرار" value={selected.decisionNotes} />
                {selected.penaltyDays || selected.penaltyAmount ? (
                  <Field
                    title="قيمة الجزاء"
                    value={[
                      selected.penaltyDays ? `${selected.penaltyDays} يوم` : "",
                      selected.penaltyAmount ? `${selected.penaltyAmount}` : "",
                    ]
                      .filter(Boolean)
                      .join(" — ")}
                  />
                ) : null}
                <Field title="ملاحظة المدير العام" value={selected.gmNote} />
              </>
            ) : (
              <Field
                title="نتيجة التحقيق"
                value={penaltyOutcomeLabel(selected)}
              />
            )}
            {selected.gmDecidedByName ? (
              <p className="inv-meta">اعتمده: {selected.gmDecidedByName}</p>
            ) : null}
          </div>

          {selected.returnNote && canManage ? (
            <div className="inv-banner">
              ملاحظة الإرجاع: {selected.returnNote}
            </div>
          ) : null}
          {actionError ? <p className="inv-error">{actionError}</p> : null}

          {canManage && selected.status === "REQUESTED" && (
            <div className="inv-form-actions">
              <button
                type="button"
                className="inv-btn danger"
                disabled={busy}
                onClick={() =>
                  runAction(() =>
                    rejectInvestigationRequest(selected.id, {
                      note: reviewNote.trim(),
                    }),
                  )
                }
              >
                رفض الطلب
              </button>
              <input
                value={reviewNote}
                onChange={(event) => setReviewNote(event.target.value)}
                placeholder="سبب الرفض (اختياري)"
              />
            </div>
          )}

          {canDecide && selected.status === "PENDING_GM" && (
            <div className="inv-section">
              <label className="inv-field">
                <span>ملاحظة المدير العام</span>
                <textarea
                  rows={3}
                  value={reviewNote}
                  onChange={(event) => setReviewNote(event.target.value)}
                  placeholder="مطلوبة عند الإرجاع، واختيارية عند الاعتماد أو الإلغاء"
                />
              </label>
              <div className="inv-actions">
                <button
                  type="button"
                  className="inv-btn primary"
                  disabled={busy}
                  onClick={() =>
                    runAction(() =>
                      reviewInvestigation(selected.id, {
                        action: "ACCEPT",
                        note: reviewNote.trim(),
                      }),
                    )
                  }
                >
                  قبول
                </button>
                <button
                  type="button"
                  className="inv-btn"
                  disabled={busy}
                  onClick={() =>
                    runAction(() =>
                      reviewInvestigation(selected.id, {
                        action: "RETURN",
                        note: reviewNote.trim(),
                      }),
                    )
                  }
                >
                  إرجاع للموارد البشرية
                </button>
                <button
                  type="button"
                  className="inv-btn danger"
                  disabled={busy}
                  onClick={() =>
                    runAction(() =>
                      reviewInvestigation(selected.id, {
                        action: "CANCEL",
                        note: reviewNote.trim(),
                      }),
                    )
                  }
                >
                  إلغاء التحقيق
                </button>
              </div>
            </div>
          )}
        </article>
      )}
    </div>
  );
}
